import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, MessageCircle } from 'lucide-react';
import C from '../../data/colors';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DashboardModals = ({ type, onClose, onNavigate }) => {
  if (type === 'sent') return <SentSuccessfullyModal onClose={onClose} />;
  if (type === 'attention') return <AttentionNeededModal onClose={onClose} />;
  if (type === 'comments') return <NewWebCommentsModal onClose={onClose} onNavigate={onNavigate} />;
  return null;
};

const Overlay = ({ children }) => (
  <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
    {children}
  </div>
);

const ModalBox = ({ title, icon, onClose, children, footer }) => (
  <Overlay>
    <div data-testid="dashboard-modal" style={{ background: C.bgCard, borderRadius:12, width:'100%', maxWidth:600, maxHeight:'80vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.4)', border: `1px solid ${C.borderMedium}` }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom: `1px solid ${C.borderMedium}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:18, fontWeight:600, color: C.textPrimary }}>{icon} {title}</div>
        <button onClick={onClose} style={{ background: C.bgCard, border: `1px solid ${C.borderMedium}`, borderRadius:6, width:32, height:32, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color: C.textSecondary }}><X size={16}/></button>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>{children}</div>
      {footer && <div style={{ padding:'12px 20px', borderTop: `1px solid ${C.borderMedium}` }}>{footer}</div>}
    </div>
  </Overlay>
);

/* ===================== SENT SUCCESSFULLY MODAL ===================== */
const SentSuccessfullyModal = ({ onClose }) => {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    const load = async () => {
      try {
        const [emailRes, smsRes] = await Promise.all([
          axios.get(`${API}/api/email-logs`),
          axios.get(`${API}/api/sms-logs`),
        ]);
        const emails = emailRes.data.filter(l => l.status === 'sent' || l.status === 'delivered').map(l => ({ ...l, channel: 'Email' }));
        const sms = smsRes.data.filter(l => l.status === 'sent' || l.status === 'delivered').map(l => ({ ...l, channel: 'SMS' }));
        setLogs([...emails, ...sms].sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || '')));
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  return (
    <ModalBox title={`Sent Successfully (${logs.length})`} icon={<CheckCircle size={20} color="#22c55e" />} onClose={onClose}
      footer={<button onClick={onClose} style={{ width:'100%', padding:12, background:C.accentPrimary, color:'#000', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>Close</button>}>
      <table style={{ width:'100%', fontSize:13, borderCollapse:'collapse' }}>
        <thead>
          <tr style={{ textAlign:'left', color: C.textSecondary, borderBottom: `1px solid ${C.borderMedium}` }}>
            <th style={{ padding:'8px 4px' }}>Recipient</th>
            <th style={{ padding:'8px 4px' }}>Contact</th>
            <th style={{ padding:'8px 4px' }}>Channel</th>
            <th style={{ padding:'8px 4px' }}>Type</th>
            <th style={{ padding:'8px 4px' }}>Status</th>
            <th style={{ padding:'8px 4px' }}>Sent At</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${C.borderMedium}` }}>
              <td style={{ padding:'8px 4px', fontWeight:500, color: C.textPrimary }}>{l.guest_name || l.recipient_name || '—'}</td>
              <td style={{ padding:'8px 4px', color: C.textSecondary }}>{l.guest_email || l.phone_number || '—'}</td>
              <td style={{ padding:'8px 4px' }}>
                <span style={{ background: l.channel==='Email' ? 'rgba(249,115,22,0.15)' : 'rgba(59,130,246,0.15)', color: l.channel==='Email' ? '#f97316' : '#60a5fa', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600 }}>{l.channel}</span>
              </td>
              <td style={{ padding:'8px 4px', color: C.textSecondary }}>{l.template_type || l.message_type || 'N/A'}</td>
              <td style={{ padding:'8px 4px' }}>
                <span style={{ color:'#22c55e', fontWeight:500 }}>{l.status === 'delivered' ? '✓ Delivered' : '↗ Sent'}</span>
              </td>
              <td style={{ padding:'8px 4px', color: C.textSecondary, fontSize:12 }}>{l.sent_at ? new Date(l.sent_at).toLocaleString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {logs.length === 0 && <div style={{ textAlign:'center', padding:24, color: C.textSecondary }}>No sent items yet.</div>}
    </ModalBox>
  );
};

/* ===================== ATTENTION NEEDED MODAL ===================== */
const AttentionNeededModal = ({ onClose }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/attention`).then(r => setItems(r.data)).catch(console.error);
  }, []);

  if (!items) return <Overlay><div style={{ color: C.textPrimary }}>Loading...</div></Overlay>;

  return (
    <ModalBox title={`Attention Needed (${items.count})`} icon={<AlertTriangle size={20} color="#ff6b6b" />} onClose={onClose}
      footer={<button onClick={onClose} style={{ width:'100%', padding:12, background:C.accentPrimary, color:'#000', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>Close</button>}>
      <p style={{ fontSize:13, color: C.textSecondary, marginBottom:16 }}>Check the box to mark issues as resolved. Count updates automatically.</p>

      {items.awaiting_save_the_date?.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:600, color:'#f97316', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
            📋 Requested Invitation - Awaiting Save The Date ({items.awaiting_save_the_date.length})
          </div>
          {items.awaiting_save_the_date.map((g, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderLeft:'3px solid #f59e0b', paddingLeft:12, marginBottom:4 }}>
              <input type="checkbox" style={{ width:16, height:16, accentColor: C.accentPrimary }} />
              <div>
                <div style={{ fontWeight:600, fontSize:14, color: C.textPrimary }}>{g.first_name} {g.last_name}</div>
                <div style={{ fontSize:12, color: C.textSecondary }}>{g.email}</div>
                <div style={{ fontSize:11, color: C.textSecondary }}>Requested: {new Date(g.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.failed_emails?.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:600, color:'#ff6b6b', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
            ✖ Failed Emails ({items.failed_emails.length})
          </div>
          {items.failed_emails.map((e, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderLeft:'3px solid #ff6b6b', paddingLeft:12, marginBottom:4 }}>
              <input type="checkbox" style={{ width:16, height:16, accentColor: C.accentPrimary }} />
              <div>
                <div style={{ fontWeight:600, fontSize:14, color: C.textPrimary }}>{e.guest_name || '—'}</div>
                <div style={{ fontSize:12, color: C.textSecondary }}>{e.guest_email}</div>
                <div style={{ fontSize:11, color:'#ff6b6b' }}>{e.error_message || 'Send failed'}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.failed_sms?.length > 0 && (
        <div>
          <div style={{ fontSize:14, fontWeight:600, color:'#ff6b6b', marginBottom:8 }}>
            ✖ Failed SMS ({items.failed_sms.length})
          </div>
          {items.failed_sms.map((s, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderLeft:'3px solid #ff6b6b', paddingLeft:12, marginBottom:4 }}>
              <input type="checkbox" style={{ width:16, height:16, accentColor: C.accentPrimary }} />
              <div>
                <div style={{ fontWeight:600, fontSize:14, color: C.textPrimary }}>{s.recipient_name || '—'}</div>
                <div style={{ fontSize:12, color: C.textSecondary }}>{s.phone_number}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.count === 0 && <div style={{ textAlign:'center', padding:24, color: C.textSecondary }}>No items need attention. Everything looks good!</div>}
    </ModalBox>
  );
};

/* ===================== NEW WEB COMMENTS MODAL ===================== */
const NewWebCommentsModal = ({ onClose, onNavigate }) => {
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/messages`).then(r => {
      const unread = r.data.filter(m => m.status === 'unread');
      setMessages(unread);
    }).catch(console.error);
  }, []);

  return (
    <ModalBox title={`New Web Comments (${messages.length})`} icon={<MessageCircle size={20} color={C.accentPrimary} />} onClose={onClose}
      footer={
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={()=>onNavigate('comments')} style={{ flex:1, padding:12, background: C.bgCard, color: C.textPrimary, border: `1px solid ${C.borderMedium}`, borderRadius:8, fontSize:13, fontWeight:500, cursor:'pointer' }}>View All Comments</button>
          <button onClick={onClose} style={{ flex:1, padding:12, background:C.accentPrimary, color:'#000', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>Close</button>
        </div>
      }>
      {messages.map((m, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom: `1px solid ${C.borderMedium}` }}>
          <div style={{ fontSize:12, color: C.textSecondary, minWidth:60 }}>{new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
          <div style={{ flex:1, fontSize:13, color: C.textPrimary }}>{m.email || m.guest_name}</div>
          <span style={{
            fontSize:11, padding:'3px 10px', borderRadius:4, fontWeight:500,
            background: m.status === 'unread' ? 'rgba(249,115,22,0.15)' : 'rgba(34,197,94,0.15)',
            color: m.status === 'unread' ? '#f97316' : '#22c55e',
          }}>
            {m.status === 'unread' ? 'Not in Read' : 'Read'}
          </span>
        </div>
      ))}
      {messages.length === 0 && <div style={{ textAlign:'center', padding:24, color: C.textSecondary }}>No new comments.</div>}
    </ModalBox>
  );
};

export default DashboardModals;
